import type { AnyClock } from "@repo/lib/clock";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import {
  PatchTenantDtoSchema,
  RoleTypeSchema,
  type RoleType,
  type Tenant,
} from "@repo/types";
import {
  cacheDefaults,
  messageServiceSubscribe,
  SubscriptionMap,
} from "./common";
import { apiService } from "@/services/apiService";
import { nullObservable } from "@repo/shared-client/commonObservables";
import { firstValueFrom, type Observable } from "rxjs";
import type { OnlyRequire } from "@repo/lib/types";
import { siteRepo } from "./siteRepo";
import { teamRepo } from "./teamRepo";
import { letterHeadRepo } from "./letterHeadRepo";
import { userRepo } from "./userRepo";
import { RepoMutationHooks } from "./repoMutationHooks";
import { baseRepo } from "./baseRepo";
import { sortCompare } from "@repo/lib/sortCompare";

type TenantGetKey = { type: "get"; tenantId: string };
type TenantGetByFriendlyKey = { type: "getByFriendly"; friendlyId: string };
type TenantListKey = { type: "list" };
type TenantKey = TenantGetKey | TenantGetByFriendlyKey | TenantListKey;

export type TenantIdPath = { tenantId: string | null };

export class TenantRepository {
  private cache: RepoCache<string, Tenant, ObjectKeyHasher, AnyClock>;
  private hooks = new RepoMutationHooks<TenantIdPath, Tenant>();
  private subscribeMap = new SubscriptionMap();
  private accessible: string[] = [];

  // expose hook registry
  addHook = this.hooks.addHook;
  removeHook = this.hooks.removeHook;

  publishNewer(tenant: Tenant) {
    this.internalPublish(tenant.id, tenant);
  }

  private internalPublish(tenantId: string, tenant: Tenant | null) {
    this.cache.publish(tenantId, tenant);

    // userRepo.list for this tenant changed: trigger update
    if (tenant) userRepo.refetchForTenant(tenant);
  }

  constructor() {
    this.cache = new RepoCache({
      requestKeyHasher: new ObjectKeyHasher({ sortKeys: false }),
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      fetch: async ({ requestKey }) => {
        const key = requestKey as TenantKey;

        switch (key.type) {
          case "get": {
            const { tenantId } = key;
            const tenant = (await apiService.v1.getTenant(tenantId)).data;
            this.internalPublish(tenantId, tenant);
            break;
          }
          case "getByFriendly": {
            const { friendlyId } = key;
            // try by friendly id, fallback to tenant id
            const tenant =
              (await apiService.v1.getTenantByFriendlyId({ friendlyId }))
                .data ?? (await apiService.v1.getTenant(friendlyId)).data;
            if (tenant) {
              this.internalPublish(tenant.id, tenant);
            }
            break;
          }
          case "list": {
            const tenants = (await apiService.v1.getAccessibleTenants()).data;
            this.accessible = [];
            for (const tenant of tenants) {
              this.accessible.push(tenant.id);
              this.internalPublish(tenant.id, tenant);
            }
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const key = requestKey as TenantKey;

        switch (key.type) {
          case "get": {
            return table.get(key.tenantId) ?? null;
          }
          case "getByFriendly": {
            for (const tenant of table.values()) {
              if (tenant.friendlyId === key.friendlyId) {
                return tenant;
              }
            }
            return table.get(key.friendlyId) ?? null;
          }
          case "list": {
            const tenants: Tenant[] = this.accessible
              .map((id) => table.get(id))
              .filter((tenant) => !!tenant)
              .sort((a, b) => sortCompare(a.name, b.name));
            return tenants;
          }
        }
      },
      affectedKeys: ({ entryData, affectsKey }) => {
        // extract
        const { id, friendlyId } = entryData;
        // affect
        affectsKey(this.getKey(id));
        affectsKey(this.getByFriendlyKey(friendlyId));
        affectsKey(this.listKey());
      },
      initialSubscribe: ({ strKey, requestKey }) => {
        const key = requestKey as TenantKey;
        if (key.type !== "get") return;

        const subscription = messageServiceSubscribe(
          "changing.tenants",
          [key.tenantId],
          () => this.cache.fetch(key)
        );

        this.subscribeMap.addSubscription(strKey, subscription);
      },
      finalUnsubscribe: ({ strKey }) => {
        this.subscribeMap.unsubscribe(strKey);
      },
    });

    baseRepo.addRepo(this);
  }

  private getKey(tenantId: string): TenantGetKey {
    return { type: "get", tenantId };
  }
  private getByFriendlyKey(friendlyId: string): TenantGetByFriendlyKey {
    return { type: "getByFriendly", friendlyId };
  }
  private listKey(): TenantListKey {
    return { type: "list" };
  }

  get(
    tenantId: string,
    options?: GetObservableOptions
  ): Observable<Tenant | null> {
    if (!tenantId) {
      return nullObservable;
    }
    const key = this.getKey(tenantId);
    return this.cache.observable(key, options);
  }
  getCached(tenantId: string): Tenant | null {
    const key = this.getKey(tenantId);
    return this.cache.collectIfUnexpired(key, null);
  }

  getByFriendly(
    friendlyId: string,
    options?: GetObservableOptions
  ): Observable<Tenant | null> {
    if (!friendlyId) {
      return nullObservable;
    }
    const key = this.getByFriendlyKey(friendlyId);
    return this.cache.observable(key, options);
  }
  getByFriendlyCached(friendlyId: string): Tenant | null {
    const key = this.getByFriendlyKey(friendlyId);
    return this.cache.collectIfUnexpired(key, null);
  }

  list(options?: GetObservableOptions): Observable<Tenant[]> {
    const key = this.listKey();
    return this.cache.observable(key, options);
  }
  listCached(): Tenant[] | null {
    const key = this.listKey();
    return this.cache.collectIfUnexpired(key, null);
  }

  async create(
    data: OnlyRequire<Tenant, "name" | "friendlyId">
  ): Promise<Tenant> {
    const hooks = this.hooks.execute({ tenantId: null }, "create");
    data = (await hooks.before(data)) as OnlyRequire<
      Tenant,
      "name" | "friendlyId"
    >;

    const {
      name,
      friendlyId,
      organizationState,
      organizationMunicipalityId,
      organizationMunicipalityName,
      organizationMunicipalityPopulation,
      organizationMunicipalityType,
      organizationMunicipalityTypeId,
    } = data;

    let created = (
      await apiService.v1.createNewTenant({
        tenantData: {
          name,
          friendlyId,
          organizationState,
          organizationMunicipalityId,
          organizationMunicipalityName,
          organizationMunicipalityPopulation,
          organizationMunicipalityType,
          organizationMunicipalityTypeId,
        },
      })
    ).data;
    created = await hooks.after(created);

    await siteRepo.create(created.id, siteRepo.init());

    // publish new tenant
    this.accessible.push(created.id);
    this.internalPublish(created.id, created);

    return created;
  }

  async update(tenantId: string, data: Partial<Tenant>) {
    if (!tenantId) {
      throw new Error("Failed to update tenant: tenantId is blank.");
    }

    const hooks = this.hooks.execute({ tenantId }, "update");
    data = await hooks.before(data);

    let updated = (
      await apiService.v1.updateTenant(
        tenantId,
        PatchTenantDtoSchema.parse(data)
      )
    ).data;

    updated = await hooks.after(updated);

    // publish updated tenant
    this.internalPublish(updated.id, updated);

    return updated;
  }

  /** Add the user by id to the tenant. Force-reloads and saves the tenant, to ensure it is
   * updated with the latest data. */
  async addUser(
    tenantId: string,
    userId: string,
    newRole: RoleType
  ): Promise<Tenant> {
    const tenant = await firstValueFrom(
      this.get(tenantId, { forceReload: true })
    );
    if (!tenant) throw new Error("addUser: Tenant not found.");

    const addRemoveUserId = (array: string[], role: RoleType) => {
      const i = array.indexOf(userId);
      if (newRole === role) {
        if (i < 0) array.push(userId);
      } else {
        if (i > -1) array.splice(i, 1);
      }
    };

    let { adminUsers, editUsers, submitUsers, viewUsers, noneUsers } = tenant;

    addRemoveUserId((adminUsers ??= []), "admin");
    addRemoveUserId((editUsers ??= []), "edit");
    addRemoveUserId((submitUsers ??= []), "submit");
    addRemoveUserId((viewUsers ??= []), "view");
    addRemoveUserId((noneUsers ??= []), "none");

    // grant the user access to the tenant
    await apiService.v1.addUserToTenant(tenantId, { userId });

    const updated = await this.update(tenantId, {
      adminUsers,
      editUsers,
      submitUsers,
      viewUsers,
      noneUsers,
    });

    return updated;
  }

  /** Removes the user with the given id from the tenant, but nothing else. For most cases, use
   * `removeUser`, which removes the user from the tenant as well as teams and letterheads. */
  async removeUserRaw(tenantId: string, userId: string): Promise<Tenant> {
    const tenant = await firstValueFrom(
      this.get(tenantId, { forceReload: true })
    );
    if (!tenant) throw new Error("removeUser: Tenant not found.");

    const removeUserId = (array: string[]) => {
      const i = array.indexOf(userId);
      if (i > -1) array.splice(i);
    };

    let { adminUsers, editUsers, submitUsers, viewUsers, noneUsers } = tenant;

    removeUserId((adminUsers ??= []));
    removeUserId((editUsers ??= []));
    removeUserId((submitUsers ??= []));
    removeUserId((viewUsers ??= []));
    removeUserId((noneUsers ??= []));

    const updated = await this.update(tenantId, {
      adminUsers,
      editUsers,
      submitUsers,
      viewUsers,
      noneUsers,
    });

    // revoke access from the user
    await apiService.v1.removeUserFromTenant(tenantId, userId);

    return updated;
  }

  /** Removes the user from the tenant, and from all its teams and letterheads. */
  async removeUser(tenantId: string, userId: string) {
    // remove user from all teams
    const teams = await firstValueFrom(teamRepo.list(tenantId));
    for (const team of teams) {
      if (
        team.allUsers.includes(userId) ||
        team.teamMembers.some((member) => member.userId === userId)
      ) {
        await teamRepo.removeMember(tenantId, team.id, userId);
      }
    }

    // remove user from all letterheads
    const letterHeads = (
      await firstValueFrom(letterHeadRepo.list(tenantId))
    ).filter((letterHead) =>
      letterHead.members.some((member) => member.userId === userId)
    );
    for (const letterHead of letterHeads) {
      await letterHeadRepo.update(tenantId, letterHead.id, {
        members: letterHead.members.filter(
          (member) => member.userId !== userId
        ),
      });
    }

    // remove user from tenant
    await this.removeUserRaw(tenantId, userId);
  }

  extractUserIds(tenant: Tenant): string[] {
    const userIds = new Set<string>();

    for (const role of RoleTypeSchema.options) {
      const array = tenant[(role + "Users") as `${RoleType}Users`];
      if (Array.isArray(array)) {
        for (const id of array) userIds.add(id);
      }
    }

    return [...userIds];
  }

  invalidate(tenant: OnlyRequire<Tenant, "id">) {
    this.cache.invalidateEntry(tenant.id);
  }

  invalidateAll() {
    this.cache.clear();
  }

  init(data?: Partial<Tenant>) {
    return baseRepo.init({
      friendlyId: "",
      ownerId: "",
      name: "",
      adminUsers: [],
      editUsers: [],
      submitUsers: [],
      viewUsers: [],
      noneUsers: [],
      standardTags: [],
      ...data,
    });
  }
}

export const tenantRepo = new TenantRepository();
